import { FormattedMessage, useIntl } from "react-intl";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useMutation,
  useRelayEnvironment,
} from "react-relay";
import { generatePath } from "react-router";
import { logger } from "@/common/logger";
import { Edit03Icon } from "@/icons";
import { Form } from "@/kit/ui/form";
import { Link } from "@/kit/ui/link";
import {
  ResponsiveSheet,
  ResponsiveSheetBody,
  ResponsiveSheetContent,
  ResponsiveSheetPortal,
  ResponsiveSheetProps,
} from "@/kit/ui/responsive-sheet";
import { relayErrorMessage } from "@/utils/relay";
import { toast } from "@/utils/toast";
import { useValibotForm } from "@/utils/validation";
import { assertNever, invariant } from "@/utils/helpers";
import {
  DatasetAsideContentSheetFooter,
  DatasetAsideContentSheetHeader,
} from "../DatasetAsideContentSheet";
import { DatasetVersionEditForm } from "./EditForm";
import { schema, AuthorSchemaInput } from "./schema";
import { fetchAllTags } from "@/utils/taggable";
import { fetchAllAuthors } from "@/utils/dataset";
import { DatasetVersionEditSheet$key } from "./__generated__/DatasetVersionEditSheet.graphql";
import { DatasetVersionEditSheetMutation$variables } from "./__generated__/DatasetVersionEditSheetMutation.graphql";
import { DatasetVersionEditSheetQuery } from "./__generated__/DatasetVersionEditSheetQuery.graphql";
import Suspense from "@/common/Suspense";
import { cn } from "@/utils/tailwind";
import { deepIsEqual } from "@/utils/form";
import { splitDatasetSlug } from "@/utils/routing";

const Fragment = graphql`
  fragment DatasetVersionEditSheet on DatasetVersion {
    id
    version
    dataset {
      slug
    }
  }
`;

const Query = graphql`
  query DatasetVersionEditSheetQuery($datasetVersionId: ID!) {
    node(id: $datasetVersionId) {
      ... on DatasetVersion @alias(as: "datasetVersion") {
        id
        license
        version
        dataset {
          id
          slug
          shortDescription
          ...taggableFragment
        }
        ...datasetVersionAuthorsFragment
      }
    }
  }
`;

const Mutation = graphql`
  mutation DatasetVersionEditSheetMutation(
    $datasetId: ID!
    $datasetVersionId: ID!
    $shortDescription: String
    $tags: [String!]
    $license: String
    $authors: [DatasetVersionAuthorInput!]
  ) {
    updateDataset(
      datasetId: $datasetId
      input: { shortDescription: $shortDescription, tags: $tags }
    ) {
      id
      ...taggableFragment
    }
    updateDatasetVersion(
      datasetVersionId: $datasetVersionId
      input: { license: $license, authors: $authors }
    ) {
      id
      ...DatasetVersionEditSheet
      ...DatasetVersionAsideContentFragment
      ...datasetVersionAuthorsFragment
    }
  }
`;

type Props = ResponsiveSheetProps & {
  datasetVersion: DatasetVersionEditSheet$key;
  canEdit?: boolean;
};

export function DatasetVersionEditSheet(props: Props) {
  const {
    datasetVersion: datasetVersionFragment,
    canEdit,
    ...sheetProps
  } = props;

  const datasetVersion = useFragment(Fragment, datasetVersionFragment);

  return (
    <div className="flex items-center justify-between">
      <p className="text-base font-semibold text-grey-900 leading-normal">
        <FormattedMessage defaultMessage="Dataset Card" />
      </p>
      {canEdit && (
        <Link
          to={generatePath("/datasets/:owner/:slug/versions/:version/edit", {
            ...splitDatasetSlug(datasetVersion.dataset.slug),
            version: datasetVersion.version,
          })}
          className="gap-1"
          variant="link"
          size="sm"
          preventScrollReset
        >
          <Edit03Icon className="w-5 h-5" />
          <FormattedMessage defaultMessage="Edit" />
        </Link>
      )}

      <ResponsiveSheet {...sheetProps}>
        <ResponsiveSheetPortal>
          <ResponsiveSheetContent className="h-full">
            <Suspense>
              <SheetContent
                datasetVersionId={datasetVersion.id}
                onOpenChange={sheetProps.onOpenChange}
              />
            </Suspense>
          </ResponsiveSheetContent>
        </ResponsiveSheetPortal>
      </ResponsiveSheet>
    </div>
  );
}

interface SheetContentProps {
  datasetVersionId: ID;
  onOpenChange?: (isOpen: boolean) => void;
}

function SheetContent(props: SheetContentProps) {
  const relay = useRelayEnvironment();
  const intl = useIntl();
  const {
    node: { datasetVersion },
  } = useLazyLoadQuery<DatasetVersionEditSheetQuery>(Query, {
    datasetVersionId: props.datasetVersionId,
  });
  const [commitMutation, isMutationInFlight] = useMutation(Mutation);

  const form = useValibotForm(schema(intl), {
    mode: "onChange",
    async defaultValues() {
      invariant(!!datasetVersion);

      const [tags, authors] = await Promise.all([
        fetchAllTags(relay, datasetVersion.dataset),
        fetchAllAuthors(relay, datasetVersion),
      ]);

      return {
        shortDescription: datasetVersion.dataset.shortDescription,
        license: datasetVersion.license,
        tags: tags.map((tag) => tag.node.name),
        authors: authors.map(({ node }): AuthorSchemaInput => {
          invariant(node.__typename !== "%other");
          switch (node.__typename) {
            case "User":
              return {
                registered: { id: node.id, displayName: node.displayName },
              };
            case "UnregisteredUser":
              return { unregistered: { fullName: node.fullName } };
            default:
              assertNever(node);
          }
        }),
      };
    },
  });

  if (!datasetVersion) {
    return null;
  }

  const doSubmit = form.handleSubmit((data) => {
    const variables: DatasetVersionEditSheetMutation$variables = {
      datasetId: datasetVersion.dataset.id,
      datasetVersionId: datasetVersion.id,
      shortDescription: data.shortDescription,
      license: data.license,
      tags: deepIsEqual(data.tags, form.formState.defaultValues?.tags)
        ? undefined
        : data.tags
          ? data.tags
          : null,
      authors: deepIsEqual(data.authors, form.formState.defaultValues?.authors)
        ? undefined
        : data.authors
          ? data.authors.map((author) => {
              if ("registered" in author) {
                return { registered: author.registered.id };
              } else {
                return author;
              }
            })
          : null,
    };
    commitMutation({
      variables,
      onCompleted() {
        toast.success(
          <FormattedMessage defaultMessage="Dataset card updated" />,
        );
        props.onOpenChange?.(false);
        form.reset(undefined, { keepDefaultValues: false });
      },
      onError(error) {
        logger.error(error);
        relayErrorMessage(error);
      },
    });
  });

  return (
    <Form
      form={form}
      onSubmit={doSubmit}
      aria-disabled={isMutationInFlight || form.formState.isLoading}
      className={cn(
        "h-full flex flex-col",
        form.formState.isLoading && "opacity-50 pointer-events-none",
      )}
    >
      <DatasetAsideContentSheetHeader
        title={<FormattedMessage defaultMessage="Edit dataset card" />}
      />
      <ResponsiveSheetBody className="w-full flex flex-col gap-6 flex-nowrap">
        <DatasetVersionEditForm />
      </ResponsiveSheetBody>
      <DatasetAsideContentSheetFooter
        disabled={
          isMutationInFlight ||
          !form.formState.isValid ||
          form.formState.isLoading
        }
      />
    </Form>
  );
}
